/**
 * HTML-based report print/download — proper Arabic / Unicode rendering.
 */
import { getClinicWebsiteBase } from '../config/siteUrls';
import { PHYSICIAN_SIGNATURE_PATH } from './pdfSignature';
import {
    isReportSectionIncluded,
    localizeReportBlocks,
    REPORT_SECTION_INCLUSION_KEYS,
    translateReportDocumentTitle,
    translateReportSectionTitle,
} from './reportLabels';

const CLINIC = {
    name: { en: 'Dr. Mahmoud Sami Abouzeid', ar: 'د. محمود سامي أبوزيد' },
    title1: { en: 'Consultant Ophthalmologist', ar: 'استشاري طب وجراحة العيون' },
    title2: { en: 'Fellow of Royal College of Physicians & Surgeons of Glasgow, UK', ar: 'زميل الكلية الملكية للجراحين' },
    title3: { en: 'Fellow of International Council of Ophthalmology, Switzerland', ar: 'زميل المجلس الدولي لطب العيون' },
    address: {
        en: '1 Zaki Nabawi - Alkawthar tower - Tersa, Haram',
        ar: '1 زكي نبوي - برج الكوثر - ترسا، الهرم',
    },
    phone: '01005602267',
};

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const formatBodyHtml = (text) => escapeHtml(text).replace(/\n/g, '<br/>');

const hasArabicText = (value) => /[\u0600-\u06FF]/.test(String(value || ''));

export const reportNeedsHtmlExport = (report, isArabic = false) => {
    if (isArabic || report?.language === 'ar') return true;
    if (hasArabicText(report?.content)) return true;
    const blocks = report?.pdfBlocks || [];
    return blocks.some((b) => hasArabicText(b.body) || hasArabicText(b.title));
};

const getPatientName = (patient) => {
    if (!patient) return '';
    if (typeof patient === 'string') return patient;
    return patient.name || patient.patientName || '';
};

const getPatientId = (patient) => {
    if (!patient || typeof patient === 'string') return '';
    return patient.id || patient.patientId || '';
};

const el = (tag, cls, inner) => `<${tag} class="${cls}">${inner}</${tag}>`;

const renderSectionBlock = (block, lang) => {
    const displayTitle = block.title ? translateReportSectionTitle(block.title, lang) : null;
    const sectionTitle = displayTitle
        ? `<p class="section-title">${escapeHtml(displayTitle)}</p>`
        : '';
    const content = el('div', 'section-content', formatBodyHtml(block.body));
    return el('div', 'report-section', sectionTitle + content);
};

export const buildReportPrintHtml = (report, patient, options = {}) => {
    const lang = options.isArabic || report?.language === 'ar' ? 'ar' : 'en';
    const isRTL = lang === 'ar';
    const dir = isRTL ? 'rtl' : 'ltr';
    const rawBlocks = report?.pdfBlocks?.length
        ? report.pdfBlocks
        : report?.content
          ? [{ type: 'body', title: null, body: report.content }]
          : [];
    const blocks = isRTL ? localizeReportBlocks(rawBlocks, 'ar') : rawBlocks;

    const rawTitle = report?.title || report?.type || (isRTL ? 'تقرير طبي' : 'Medical Report');
    const title = isRTL ? translateReportDocumentTitle(rawTitle, 'ar') : rawTitle;
    const reportDate = report?.date || new Date().toISOString().split('T')[0];
    const patientName = getPatientName(patient);
    const patientId = getPatientId(patient);

    const blocksHtml = blocks.map((block) => renderSectionBlock(block, lang)).join('');

    const signature =
        isReportSectionIncluded(REPORT_SECTION_INCLUSION_KEYS.SIGNATURE, report?.sectionInclusion) &&
        report?.signature?.trim()
            ? report.signature.trim()
            : '';
    const dateLabel = isRTL ? 'التاريخ' : 'Date';
    const idLabel = isRTL ? 'رقم المريض' : 'Patient ID';
    const portalHint = patientId
        ? `${getClinicWebsiteBase()}/portal.html#id=${encodeURIComponent(patientId)}`
        : getClinicWebsiteBase();

    const padSide = isRTL ? 'right' : 'left';

    const includeSignature = isReportSectionIncluded(
        REPORT_SECTION_INCLUSION_KEYS.SIGNATURE,
        report?.sectionInclusion
    );
    const signatureHtml = includeSignature
        ? `<div class="signature-block signature-block--image">
    <img src="${escapeHtml(PHYSICIAN_SIGNATURE_PATH)}" alt="Physician signature" class="signature-image" />
    ${signature ? `<div class="signature-text">${formatBodyHtml(signature)}</div>` : ''}
  </div>`
        : '';

    return `<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
  <meta charset="utf-8" />
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;700&display=swap" rel="stylesheet" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { margin: 40mm 6mm 58mm 8mm; size: A5 portrait; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: ${isRTL ? "'Noto Sans Arabic', 'Segoe UI', Tahoma, 'Arial Unicode MS', serif" : "'Times New Roman', Georgia, serif"};
      font-size: 8.5pt;
      line-height: 1.32;
      color: #111;
      max-width: 148mm;
      margin: 0 auto;
    }
    .report-header {
      text-align: center;
      border-bottom: 1.5px solid #0e7490;
      padding-bottom: 0.35rem;
      margin-bottom: 0.35rem;
    }
    .clinic-name { font-size: 10.5pt; font-weight: bold; margin: 0; color: #0e7490; line-height: 1.2; }
    .clinic-subtitle { font-size: 7pt; margin: 0.05rem 0; line-height: 1.2; }
    .clinic-contact { font-size: 6.5pt; color: #555; margin: 0.1rem 0 0; }
    .report-title {
      text-align: center;
      font-size: 9.5pt;
      font-weight: bold;
      margin: 0.25rem 0 0.35rem;
      text-transform: uppercase;
      line-height: 1.2;
    }
    .meta-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem 0.85rem;
      margin-bottom: 0.35rem;
      font-size: 7.5pt;
      padding: 0.2rem 0.35rem;
      background: #f0fdfa;
      border: 1px solid #99f6e4;
    }
    .meta-item strong { margin-${padSide}: 0.25rem; }
    .clinical-body { column-count: 1; column-gap: 0.5rem; }
    .report-section {
      margin-bottom: 0.3rem;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .section-title {
      font-weight: bold;
      font-size: 8pt;
      text-decoration: underline;
      margin: 0 0 0.12rem;
      line-height: 1.2;
    }
    .section-content {
      white-space: pre-wrap;
      font-size: 8pt;
      line-height: 1.28;
      padding-${padSide}: 0.25rem;
    }
    .signature-block {
      margin-top: 0.45rem;
      text-align: ${isRTL ? 'left' : 'right'};
      white-space: pre-wrap;
      font-size: 7.5pt;
      line-height: 1.25;
    }
    .signature-image {
      display: block;
      max-width: 50mm;
      height: auto;
      margin-${isRTL ? 'right' : 'left'}: auto;
      margin-${isRTL ? 'left' : 'right'}: 0;
    }
    .signature-text { margin-top: 0.2rem; }
    .footer-note {
      margin-top: 0.35rem;
      font-size: 6pt;
      color: #666;
      text-align: center;
      border-top: 1px solid #ddd;
      padding-top: 0.2rem;
    }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  ${el(
      'div',
      'report-header',
      `<p class="clinic-name">${escapeHtml(CLINIC.name[lang])}</p>
    <p class="clinic-subtitle">${escapeHtml(CLINIC.title1[lang])}</p>
    <p class="clinic-subtitle">${escapeHtml(CLINIC.title2[lang])}</p>
    <p class="clinic-subtitle">${escapeHtml(CLINIC.title3[lang])}</p>
    <p class="clinic-contact">${escapeHtml(CLINIC.address[lang])} · ${escapeHtml(CLINIC.phone)}</p>`
  )}
  <h1 class="report-title">${escapeHtml(title)}</h1>
  ${el(
      'div',
      'meta-row',
      `<span class="meta-item"><strong>${escapeHtml(isRTL ? 'المريض:' : 'Patient:')}</strong>${escapeHtml(patientName)}</span>
    <span class="meta-item"><strong>${escapeHtml(idLabel)}:</strong>${escapeHtml(patientId)}</span>
    <span class="meta-item"><strong>${escapeHtml(dateLabel)}:</strong>${escapeHtml(reportDate)}</span>`
  )}
  ${el('div', 'clinical-body', blocksHtml)}
  ${signatureHtml}
  <p class="footer-note">${escapeHtml(isRTL ? 'سجلات المريض:' : 'Patient portal:')} ${escapeHtml(portalHint)}</p>
</body>
</html>`;
};

const openPrintWindow = (html, forPrint = true) => {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
        alert('Please allow pop-ups to print this report.');
        return null;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    if (forPrint) {
        setTimeout(() => {
            win.print();
        }, 400);
    }
    return win;
};

export const printReportHtml = (report, patient, options = {}) => {
    const html = buildReportPrintHtml(report, patient, options);
    return openPrintWindow(html, true);
};

export const downloadReportHtml = (report, patient, options = {}) => {
    const html = buildReportPrintHtml(report, patient, options);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const patientSlug = (getPatientName(patient) || 'patient').replace(/\s+/g, '_');
    link.href = url;
    link.download = `report_${patientSlug}_${report?.id || Date.now()}.html`;
    link.click();
    URL.revokeObjectURL(url);
    return link.download;
};
